import React, { useEffect, useMemo, useRef, useState } from 'react';
import PePapersDisclaimerGate from '../pe-papers/shared/PePapersDisclaimerGate';
import { createPortal } from 'react-dom';
import { Loader2, X } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { savePePapersArchiveDoc } from '../../services/pePapersArchiveService';
import useAppStore from '../../store/appStore';
import { usePersonalDataSelector } from '../../store/usePersonalDataSelector';
import { downloadHtmlAsPdf } from '../../utils/pe-papers/htmlDocumentPdfDownload';

/** Noble Slate — Vorschau/PDF/Druck (analog Änderungsvertrag) */
const AB_CONTRACT_PDF_CSS = `
  .av-a4-page {
    display: block !important;
    width: 100% !important;
    max-width: 210mm !important;
    margin-left: auto !important;
    margin-right: auto !important;
    box-sizing: border-box !important;
    font-family: Georgia, 'Times New Roman', serif !important;
    color: #0f172a !important;
    font-size: 12px !important;
    line-height: 1.48 !important;
    padding: 11mm 13mm !important;
    background: #ffffff !important;
  }
  .av-a4-page * { box-sizing: border-box !important; }
  .av-a4-page .pdf-letterhead {
    width: 100% !important;
    text-align: center !important;
    border-bottom: 2px solid #1e293b !important;
    margin: 0 0 12px !important;
    padding: 0 0 10px !important;
  }
  .av-a4-page .pdf-letterhead h1 {
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 20px !important;
    font-weight: 900 !important;
    letter-spacing: 0.12em !important;
    text-transform: uppercase !important;
    color: #0f172a !important;
    background: transparent !important;
    border: none !important;
    padding: 0 !important;
    margin: 0 0 3px !important;
    text-align: center !important;
  }
  .av-a4-page .pdf-letterhead h2 {
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 10.5px !important;
    font-weight: 700 !important;
    letter-spacing: 0.16em !important;
    text-transform: uppercase !important;
    color: #b48a56 !important;
    background: transparent !important;
    border: none !important;
    padding: 0 !important;
    margin: 0 !important;
    text-align: center !important;
  }
  .av-meta-grid {
    display: table !important;
    width: 100% !important;
    table-layout: fixed !important;
    margin: 0 0 12px !important;
    border: 1px solid #cbd5e1 !important;
    background: #f8fafc !important;
  }
  .av-meta-col {
    display: table-cell !important;
    width: 50% !important;
    vertical-align: top !important;
    padding: 10px 12px !important;
    font-size: 12px !important;
    line-height: 1.4 !important;
    color: #334155 !important;
    text-align: left !important;
  }
  .av-meta-col + .av-meta-col { border-left: 1px solid #e2e8f0 !important; }
  .av-meta-label {
    display: block !important;
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 11px !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.12em !important;
    color: #64748b !important;
    margin: 0 0 4px !important;
  }
  .av-meta-col strong { color: #0f172a !important; font-weight: 700 !important; }
  .av-meta-col p {
    width: 100% !important;
    margin: 0 !important;
    text-align: left !important;
    font-size: 12px !important;
    line-height: 1.4 !important;
    color: #334155 !important;
  }
  .av-meta-col .av-meta-date {
    display: block !important;
    margin-top: 4px !important;
    color: #64748b !important;
    font-size: 11px !important;
  }
  .av-doc-section {
    display: block !important;
    width: 100% !important;
    margin: 0 0 12px !important;
  }
  .av-a4-page h3,
  .av-doc-section > h3 {
    display: block !important;
    width: 100% !important;
    font-family: Arial, Helvetica, sans-serif !important;
    font-size: 11.5px !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.05em !important;
    text-align: left !important;
    background: #1e293b !important;
    color: #f8fafc !important;
    border: none !important;
    border-radius: 0 !important;
    padding: 6px 10px !important;
    margin: 0 0 8px !important;
  }
  .av-a4-page p,
  .av-doc-p,
  .av-doc-section p {
    display: block !important;
    width: 100% !important;
    max-width: 100% !important;
    font-family: Georgia, 'Times New Roman', serif !important;
    font-size: 12px !important;
    line-height: 1.48 !important;
    color: #1e293b !important;
    text-align: justify !important;
    margin: 0 0 8px !important;
  }
  .av-warn-box {
    display: block !important;
    width: 100% !important;
    border: 1px solid #cbd5e1 !important;
    background: #f8fafc !important;
    padding: 8px 10px !important;
    margin: 0 0 10px !important;
    font-family: Georgia, 'Times New Roman', serif !important;
    font-size: 12px !important;
    line-height: 1.45 !important;
    color: #1e293b !important;
  }
  .ab-sig-table {
    width: 100% !important;
    border-collapse: collapse !important;
    table-layout: fixed !important;
    margin-top: 28px !important;
    border: none !important;
  }
  .ab-sig-table td {
    width: 50% !important;
    vertical-align: top !important;
    border: none !important;
    background: transparent !important;
    padding: 0 !important;
  }
  .ab-sig-table p {
    text-align: left !important;
    margin: 0 !important;
    width: auto !important;
    max-width: 100% !important;
    font-family: Arial, Helvetica, sans-serif !important;
  }
  .ab-sig-line-soft {
    display: block !important;
    width: 200px !important;
    max-width: 100% !important;
    border: none !important;
    border-bottom: 2px solid #1e293b !important;
    margin-top: 0 !important;
    margin-bottom: 6px !important;
    height: 0 !important;
    padding: 0 !important;
    background: transparent !important;
  }
  @media print {
    @page { size: A4; margin: 0; }
    body { margin: 0 !important; padding: 0 !important; background: #fff !important; }
    .av-a4-page {
      box-shadow: none !important;
      margin: 0 !important;
      max-width: none !important;
      width: 100% !important;
      min-height: 0 !important;
    }
  }
`;

const AB_PRINT_CSS = `
${AB_CONTRACT_PDF_CSS}
html, body {
  margin: 0 !important;
  padding: 0 !important;
  background: #fff !important;
}
`;

interface AbmahnungModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialData?: any;
    onArchived?: () => void;
}

type DokumentTyp = 'ermahnung' | 'abmahnung';

type StandardgrundKey =
    | 'unpuenktlichkeit'
    | 'unentschuldigtes_fehlen'
    | 'salon_ordnung'
    | 'schlechtleistung'
    | 'verhalten_kunden'
    | 'sonstiges'
    | '';

interface BetriebFormData {
    firmenname: string;
    strasse: string;
    plz: string;
    ort: string;
}

interface MitarbeiterFormData {
    name: string;
    strasse: string;
    plz: string;
    ort: string;
}

interface BetriebFieldLocks {
    firmenname: boolean;
    strasse: boolean;
    plz: boolean;
    ort: boolean;
}

type TeamPool = 'produktiv' | 'unproduktiv' | 'azubi';

type TeamEmployeeOption = {
    id: string;
    name: string;
    pool: TeamPool;
    adresse: string;
};

const INITIAL_MITARBEITER_DATA: MitarbeiterFormData = {
    name: '',
    strasse: '',
    plz: '',
    ort: '',
};

const STANDARDGRUND_OPTIONS: { key: Exclude<StandardgrundKey, ''>; label: string }[] = [
    { key: 'unpuenktlichkeit', label: 'Unpünktlichkeit' },
    { key: 'unentschuldigtes_fehlen', label: 'Unentschuldigtes Fehlen' },
    { key: 'salon_ordnung', label: 'Verstoß gegen die Salon-Ordnung/Hygieneregeln' },
    { key: 'schlechtleistung', label: 'Schlechtleistung/Arbeitsverweigerung' },
    { key: 'verhalten_kunden', label: 'Ungebührliches Verhalten gegenüber Kunden' },
    { key: 'sonstiges', label: 'Sonstiges / Eigener Grund' },
];

const KI_LEKTORAT_HINWEIS =
    'Hinweis: Dein eingetragener Sachverhalt wird bei der Dokumentenerstellung automatisch per KI lektoriert, um eine professionelle, sachliche und rechtssichere Formulierung zu garantieren.';

const ABMAHNUNG_AUFFORDERUNG =
    'Wir fordern Sie auf, dieses vertragswidrige Verhalten unverzüglich einzustellen und Ihren arbeitsvertraglichen Pflichten künftig uneingeschränkt nachzukommen.';

const ABMAHNUNG_AUFFORDERUNG_AZUBI =
    'Wir fordern Sie auf, dieses vertragswidrige Verhalten unverzüglich einzustellen und Ihren ausbildungsvertraglichen Pflichten künftig uneingeschränkt nachzukommen.';

const ABMAHNUNG_WARNFUNKTION =
    'Warnfunktion: Sollten Sie künftig erneut gegen Ihre arbeitsvertraglichen Pflichten verstoßen, müssen Sie mit weiteren arbeitsrechtlichen Konsequenzen bis hin zur ordentlichen oder außerordentlichen (fristlosen) Kündigung des Arbeitsverhältnisses rechnen. Ein Doppel dieser Abmahnung wird zu Ihrer Personalakte genommen.';

const ABMAHNUNG_WARNFUNKTION_AZUBI =
    'Warnfunktion: Sollten Sie künftig erneut gegen Ihre ausbildungsvertraglichen Pflichten verstoßen, müssen Sie mit weiteren arbeitsrechtlichen Konsequenzen bis hin zur ordentlichen oder außerordentlichen (fristlosen) Kündigung des Ausbildungsverhältnisses rechnen. Ein Doppel dieser Abmahnung wird zu Ihrer Personalakte genommen.';

const ERMAHNUNG_WIEDERHOLUNGSHINWEIS =
    'Wir weisen Sie vorsorglich darauf hin, dass ein wiederholter Verstoß gegen Ihre arbeitsvertraglichen Pflichten eine formelle Abmahnung nach sich ziehen wird.';

const A4_PREVIEW_PAGE_CLASS =
    'av-a4-page bg-white text-slate-900 my-4 mx-auto w-full max-w-[210mm] h-auto min-h-0 shadow-xl rounded-sm text-left block overflow-visible print:my-0 print:shadow-none';

const DOC_P_CLASS = 'av-doc-p mb-2 text-justify text-slate-800 leading-relaxed';

const INPUT_CLASS =
    'w-full rounded-lg border border-pe-border dark:border-slate-700 bg-pe-input dark:bg-black text-pe-text dark:text-white px-4 py-2.5 text-sm focus:outline-none focus:border-[#E6C299]/50 transition-colors';

const LABEL_CLASS = 'text-xs font-semibold text-pe-muted dark:text-slate-400 uppercase tracking-wider mb-1 block';

const READONLY_INPUT_CLASS =
    'w-full rounded-lg border border-pe-border dark:border-slate-700 bg-pe-elevated dark:bg-[#0a0a0a] text-pe-accent-text dark:text-[#E6C299] px-4 py-2.5 text-sm cursor-not-allowed opacity-90';

const ERROR_INPUT_RING = 'border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500/20';

const STEP_LABELS: Record<number, string> = {
    1: 'Datenerfassung & Typ-Weiche',
    2: 'Vorschau & Export',
};

const isEmpty = (value: string) => !String(value || '').trim();

/** Legacy-Archiv / Stammdaten: einzeilige `adresse` → Straße / PLZ / Ort */
const parseLegacyMitarbeiterAdresse = (
    adresse: string
): Pick<MitarbeiterFormData, 'strasse' | 'plz' | 'ort'> => {
    const raw = String(adresse || '').trim();
    if (!raw) return { strasse: '', plz: '', ort: '' };

    const lines = raw
        .split(/[\n;]+/)
        .map((s) => s.trim())
        .filter(Boolean);

    const plzOrtMatch = (value: string) => {
        const m = value.match(/^(\d{4,5})\s+(.+)$/);
        return m ? { plz: m[1], ort: m[2].trim() } : null;
    };

    if (lines.length === 1) {
        const parts = lines[0]
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
        if (parts.length >= 2) {
            const last = plzOrtMatch(parts[parts.length - 1]);
            if (last) {
                return {
                    strasse: parts.slice(0, -1).join(', '),
                    plz: last.plz,
                    ort: last.ort,
                };
            }
        }
        const single = plzOrtMatch(lines[0]);
        if (single) return { strasse: '', ...single };
        return { strasse: lines[0], plz: '', ort: '' };
    }

    const lastLine = plzOrtMatch(lines[lines.length - 1]);
    if (lastLine) {
        return {
            strasse: lines.slice(0, -1).join(', '),
            plz: lastLine.plz,
            ort: lastLine.ort,
        };
    }
    return { strasse: lines[0], plz: '', ort: lines.slice(1).join(' ') };
};

const normalizeMitarbeiterData = (incoming: any): MitarbeiterFormData => {
    const src = incoming && typeof incoming === 'object' ? incoming : {};
    const hasStructured =
        !isEmpty(String(src.strasse || '')) ||
        !isEmpty(String(src.plz || '')) ||
        !isEmpty(String(src.ort || ''));
    if (!hasStructured && typeof src.adresse === 'string' && src.adresse.trim()) {
        const parsed = parseLegacyMitarbeiterAdresse(src.adresse);
        return {
            name: String(src.name || ''),
            strasse: parsed.strasse,
            plz: parsed.plz,
            ort: parsed.ort,
        };
    }
    return {
        name: String(src.name || ''),
        strasse: String(src.strasse || ''),
        plz: String(src.plz || ''),
        ort: String(src.ort || ''),
    };
};

const formatMitarbeiterAdresseInline = (data: MitarbeiterFormData): string => {
    const line1 = data.strasse.trim();
    const line2 = [data.plz.trim(), data.ort.trim()].filter(Boolean).join(' ');
    return [line1, line2].filter(Boolean).join(', ');
};

const getCallableErrorMessage = (error: unknown): string => {
    if (error && typeof error === 'object' && 'message' in error) {
        const message = (error as { message?: unknown }).message;
        if (typeof message === 'string' && message.trim()) return message;
    }
    return 'Unbekannter API-Fehler';
};

const formatTodayGerman = (): string => {
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const yyyy = now.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
};

const buildBetriebFromClient = (clientData: any): { betrieb: BetriebFormData; locks: BetriebFieldLocks } => {
    const firmenname = clientData?.salonname || clientData?.firmenname || '';
    const strasse = clientData?.strasse || '';
    const plz = clientData?.plz || '';
    const ort = clientData?.ort || '';

    return {
        betrieb: { firmenname, strasse, plz, ort },
        locks: {
            firmenname: !isEmpty(firmenname),
            strasse: !isEmpty(strasse),
            plz: !isEmpty(plz),
            ort: !isEmpty(ort),
        },
    };
};

const getStandardgrundLabel = (key: StandardgrundKey): string => {
    const option = STANDARDGRUND_OPTIONS.find((opt) => opt.key === key);
    return option?.label || '____________';
};

const AbmahnungModal: React.FC<AbmahnungModalProps> = ({ isOpen, onClose, initialData, onArchived }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [dokumentTyp, setDokumentTyp] = useState<DokumentTyp>('ermahnung');
    const [istAuszubildender, setIstAuszubildender] = useState(false);
    const [standardgrund, setStandardgrund] = useState<StandardgrundKey>('');
    const [sachverhaltFreitext, setSachverhaltFreitext] = useState('');
    const [betriebData, setBetriebData] = useState<BetriebFormData>({
        firmenname: '',
        strasse: '',
        plz: '',
        ort: '',
    });
    const [betriebLocks, setBetriebLocks] = useState<BetriebFieldLocks>({
        firmenname: false,
        strasse: false,
        plz: false,
        ort: false,
    });
    const [mitarbeiterData, setMitarbeiterData] = useState<MitarbeiterFormData>(INITIAL_MITARBEITER_DATA);
    const [selectedTeamEmployeeId, setSelectedTeamEmployeeId] = useState('');
    const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
    const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isPdfDownloading, setIsPdfDownloading] = useState(false);
    const [archiveFeedback, setArchiveFeedback] = useState<'idle' | 'success' | 'error' | 'draft'>('idle');
    const [archiveDocId, setArchiveDocId] = useState<string | null>(null);
    const [isOptimizingSachverhalt, setIsOptimizingSachverhalt] = useState(false);
    const [apiKiError, setApiKiError] = useState(false);

    const printContainerRef = useRef<HTMLDivElement>(null);

    const clientData = useAppStore((state: any) => state.clientData);
    const { personalData, unproductivePersonalData, azubiAssistenzData } = usePersonalDataSelector();

    const teamEmployees = useMemo((): TeamEmployeeOption[] => {
        const mapPool = (list: any[], pool: TeamPool): TeamEmployeeOption[] =>
            (Array.isArray(list) ? list : [])
                .filter((emp) => emp?.id)
                .map((emp) => ({
                    id: String(emp.id),
                    name: String(emp.name || '').trim() || 'Ohne Name',
                    pool,
                    adresse: String(emp.adresse || emp.info || '').trim(),
                }));
        return [
            ...mapPool(personalData, 'produktiv'),
            ...mapPool(unproductivePersonalData, 'unproduktiv'),
            ...mapPool(azubiAssistenzData, 'azubi'),
        ];
    }, [personalData, unproductivePersonalData, azubiAssistenzData]);

    useEffect(() => {
        if (!isOpen) {
            setCurrentStep(1);
            setDokumentTyp('ermahnung');
            setIstAuszubildender(false);
            setStandardgrund('');
            setSachverhaltFreitext('');
            setMitarbeiterData(INITIAL_MITARBEITER_DATA);
            setSelectedTeamEmployeeId('');
            setInvalidFields(new Set());
            setDisclaimerAccepted(false);
            setIsSaving(false);
            setArchiveFeedback('idle');
            setArchiveDocId(null);
            setIsOptimizingSachverhalt(false);
            setApiKiError(false);
            return;
        }

        const { betrieb, locks } = buildBetriebFromClient(clientData);
        setBetriebData(betrieb);
        setBetriebLocks(locks);
        setCurrentStep(1);
        setDokumentTyp('ermahnung');
        setIstAuszubildender(false);
        setStandardgrund('');
        setSachverhaltFreitext('');
        setMitarbeiterData(INITIAL_MITARBEITER_DATA);
        setSelectedTeamEmployeeId('');
        setInvalidFields(new Set());
        setDisclaimerAccepted(false);
        setIsSaving(false);
        setArchiveFeedback('idle');
        setArchiveDocId(initialData?.id || null);
        setIsOptimizingSachverhalt(false);
        setApiKiError(false);

        if (initialData) {
            if (initialData.dokumentTyp === 'abmahnung' || initialData.dokumentTyp === 'ermahnung') {
                setDokumentTyp(initialData.dokumentTyp);
            }
            setIstAuszubildender(Boolean(initialData.istAuszubildender));
            if (typeof initialData.standardgrund === 'string') {
                setStandardgrund(initialData.standardgrund as StandardgrundKey);
            }
            if (typeof initialData.sachverhaltFreitext === 'string') {
                setSachverhaltFreitext(initialData.sachverhaltFreitext);
            }
            if (initialData.betriebData && typeof initialData.betriebData === 'object') {
                setBetriebData({ ...betrieb, ...initialData.betriebData });
                setBetriebLocks({
                    firmenname: !isEmpty(String(initialData.betriebData.firmenname ?? betrieb.firmenname)),
                    strasse: !isEmpty(String(initialData.betriebData.strasse ?? betrieb.strasse)),
                    plz: !isEmpty(String(initialData.betriebData.plz ?? betrieb.plz)),
                    ort: !isEmpty(String(initialData.betriebData.ort ?? betrieb.ort)),
                });
            }
            if (initialData.mitarbeiterData && typeof initialData.mitarbeiterData === 'object') {
                setMitarbeiterData(normalizeMitarbeiterData(initialData.mitarbeiterData));
            }
            if (typeof initialData.selectedTeamEmployeeId === 'string') {
                setSelectedTeamEmployeeId(initialData.selectedTeamEmployeeId);
            }
            const step = Number(initialData.currentStep);
            if (Number.isFinite(step) && step >= 1 && step <= 2) {
                setCurrentStep(step);
            }
            if (initialData.confirmedDisclaimer) {
                setDisclaimerAccepted(true);
            }
        }
    }, [isOpen, clientData, initialData]);

    const validateStep1 = (): string[] => {
        const errors: string[] = [];

        if (isEmpty(betriebData.firmenname)) errors.push('betrieb_firmenname');
        if (isEmpty(betriebData.strasse)) errors.push('betrieb_strasse');
        if (isEmpty(betriebData.plz)) errors.push('betrieb_plz');
        if (isEmpty(betriebData.ort)) errors.push('betrieb_ort');
        if (isEmpty(mitarbeiterData.name)) errors.push('mitarbeiter_name');
        if (isEmpty(mitarbeiterData.strasse)) errors.push('mitarbeiter_strasse');
        if (isEmpty(mitarbeiterData.plz)) errors.push('mitarbeiter_plz');
        if (isEmpty(mitarbeiterData.ort)) errors.push('mitarbeiter_ort');
        if (!standardgrund) errors.push('standardgrund');
        if (isEmpty(sachverhaltFreitext)) errors.push('sachverhalt_freitext');

        return errors;
    };

    const isFieldInvalid = (field: string) => invalidFields.has(field);

    const getInputClass = (baseClass: string, field: string) =>
        isFieldInvalid(field) ? `${baseClass} ${ERROR_INPUT_RING}` : baseClass;

    const clearFieldError = (field: string) => {
        setInvalidFields((prev) => {
            if (!prev.has(field)) return prev;
            const next = new Set(prev);
            next.delete(field);
            return next;
        });
    };

    const updateBetriebField = <K extends keyof BetriebFormData>(key: K, value: BetriebFormData[K]) => {
        setBetriebData((prev) => ({ ...prev, [key]: value }));
        clearFieldError(`betrieb_${key}`);
    };

    const updateMitarbeiterField = <K extends keyof MitarbeiterFormData>(key: K, value: MitarbeiterFormData[K]) => {
        setMitarbeiterData((prev) => ({ ...prev, [key]: value }));
        clearFieldError(`mitarbeiter_${key}`);
    };

    const handleTeamEmployeeSelect = (employeeId: string) => {
        setSelectedTeamEmployeeId(employeeId);
        if (!employeeId) return;
        const emp = teamEmployees.find((e) => e.id === employeeId);
        if (!emp) return;
        const parsedAdresse = parseLegacyMitarbeiterAdresse(emp.adresse);
        setMitarbeiterData({
            name: emp.name === 'Ohne Name' ? '' : emp.name,
            strasse: parsedAdresse.strasse,
            plz: parsedAdresse.plz,
            ort: parsedAdresse.ort,
        });
        clearFieldError('mitarbeiter_name');
        clearFieldError('mitarbeiter_strasse');
        clearFieldError('mitarbeiter_plz');
        clearFieldError('mitarbeiter_ort');
        if (emp.pool === 'azubi') {
            setIstAuszubildender(true);
        }
    };

    const renderFieldError = (field: string) =>
        isFieldInvalid(field) ? (
            <p className="text-[10px] text-red-400 mt-1">Dieses Feld ist erforderlich</p>
        ) : null;

    const handleWeiter = async () => {
        const errors = validateStep1();
        if (errors.length > 0) {
            setInvalidFields(new Set(errors));
            return;
        }
        setInvalidFields(new Set());
        setIsOptimizingSachverhalt(true);
        setApiKiError(false);

        const isSonstigerGrund = standardgrund === 'sonstiges';
        const grundLabelForKi = isSonstigerGrund ? 'Sonstiges / Eigener Grund' : getStandardgrundLabel(standardgrund);

        try {
            const optimizeSachverhaltFn = httpsCallable(functions, 'optimizePePapersAbmahnungSachverhalt');
            const response = await optimizeSachverhaltFn({
                roherText: sachverhaltFreitext.trim(),
                standardgrund: grundLabelForKi,
                standardgrundKey: standardgrund,
                dokumentTyp,
                istAuszubildender,
                isCustomReason: isSonstigerGrund,
            });

            const data = response.data as { text?: string; optimizedText?: string } | null;
            const optimizedText = data?.text?.trim() || data?.optimizedText?.trim();

            if (!optimizedText) {
                throw new Error('Leere KI-Antwort');
            }

            setSachverhaltFreitext(optimizedText);
        } catch (error: unknown) {
            console.error('PEPapers Abmahnung KI-Lektorat fehlgeschlagen:', error);
            console.warn('Callable-Fehlerdetails:', getCallableErrorMessage(error));
            setApiKiError(true);
        } finally {
            setIsOptimizingSachverhalt(false);
            setCurrentStep(2);
        }
    };

    const handleZurueck = () => {
        setInvalidFields(new Set());
        setCurrentStep(1);
    };

    const handleClose = () => {
        setCurrentStep(1);
        setIstAuszubildender(false);
        setInvalidFields(new Set());
        setDisclaimerAccepted(false);
        setIsSaving(false);
        setArchiveFeedback('idle');
        setIsOptimizingSachverhalt(false);
        setApiKiError(false);
        onClose();
    };

    const isActionLocked = !disclaimerAccepted;

    const getActionButtonClass = (baseClass: string) =>
        `${baseClass} ${isActionLocked ? 'opacity-50 cursor-not-allowed' : ''}`;

    const dokumentTitel =
        dokumentTyp === 'abmahnung'
            ? istAuszubildender
                ? 'Abmahnung Auszubildende(r)'
                : 'Abmahnung'
            : istAuszubildender
              ? 'Ermahnung Auszubildende(r)'
              : 'Ermahnung';
    const dokumentTitelKurz = dokumentTyp === 'abmahnung' ? 'Abmahnung' : 'Ermahnung';
    const abmahnungAufforderung = istAuszubildender ? ABMAHNUNG_AUFFORDERUNG_AZUBI : ABMAHNUNG_AUFFORDERUNG;
    const abmahnungWarnfunktion = istAuszubildender ? ABMAHNUNG_WARNFUNKTION_AZUBI : ABMAHNUNG_WARNFUNKTION;
    const ermahnungWiederholungshinweis = istAuszubildender
        ? 'Wir weisen Sie vorsorglich darauf hin, dass ein wiederholter Verstoß gegen Ihre ausbildungsvertraglichen Pflichten eine formelle Abmahnung nach sich ziehen wird.'
        : ERMAHNUNG_WIEDERHOLUNGSHINWEIS;
    const pflichtenLabel = istAuszubildender ? 'ausbildungsvertraglichen' : 'arbeitsvertraglichen';
    const grundLabel = getStandardgrundLabel(standardgrund);
    const isSonstigerGrund = standardgrund === 'sonstiges';
    const mitarbeiterNameDoc = mitarbeiterData.name.trim() || 'Mitarbeiter/in';

    const renderSegmentedControl = <T extends string>(
        value: T,
        options: { key: T; label: string }[],
        onChange: (key: T) => void
    ) => (
        <div className="flex rounded-lg border border-pe-border dark:border-slate-700 bg-pe-input dark:bg-black p-1.5 gap-1.5">
            {options.map((opt) => (
                <button
                    key={opt.key}
                    type="button"
                    onClick={() => onChange(opt.key)}
                    className={`flex-1 min-h-[44px] px-3 py-2.5 rounded-md text-xs sm:text-sm font-bold uppercase tracking-wider transition-colors ${
                        value === opt.key
                            ? 'bg-[#E6C299]/20 text-pe-accent-text dark:text-[#E6C299] border border-[#E6C299]/40 shadow-[0_0_0_1px_rgba(230,194,153,0.25)]'
                            : 'text-stone-500 dark:text-slate-500 hover:text-stone-700 dark:hover:text-slate-300 border border-transparent'
                    }`}
                >
                    {opt.label}
                </button>
            ))}
        </div>
    );

    const getPrintHtml = () =>
        printContainerRef.current?.innerHTML ||
        document.getElementById('pepapers-abmahnung-print')?.innerHTML ||
        '';

    const handleDownloadPdf = async () => {
        if (isActionLocked || isPdfDownloading) return;
        const html = getPrintHtml();
        if (!html.trim()) {
            window.alert('Dokumentinhalt fehlt — bitte Vorschau prüfen.');
            return;
        }
        setIsPdfDownloading(true);
        try {
            const dateIso = new Date().toISOString().slice(0, 10);
            await downloadHtmlAsPdf({
                html,
                filename: `${dokumentTitel}_${dateIso}.pdf`,
                scrubMode: 'minimal',
                splitPrintBreaks: true,
                capturePadPx: 0,
                letterheadOnBreaks: false,
                extraCss: AB_CONTRACT_PDF_CSS,
            });
        } catch (err: unknown) {
            console.error('PEPapers Abmahnung PDF-Download fehlgeschlagen:', err);
            const message = err instanceof Error ? err.message : 'Unbekannter Fehler';
            window.alert('PDF konnte nicht heruntergeladen werden: ' + message);
        } finally {
            setIsPdfDownloading(false);
        }
    };

    const handlePrintIsolated = () => {
        if (isActionLocked || isPdfDownloading) return;

        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.width = '0px';
        iframe.style.height = '0px';
        iframe.style.border = 'none';
        document.body.appendChild(iframe);

        const printWindow = iframe.contentWindow;
        if (!printWindow) {
            document.body.removeChild(iframe);
            return;
        }

        const doc = printWindow.document;
        const printContent = getPrintHtml();

        doc.open();
        doc.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>${dokumentTitel}</title>
      <style>${AB_PRINT_CSS}</style>
    </head>
    <body>
      ${printContent}
    </body>
    </html>
  `);
        doc.close();

        setTimeout(() => {
            printWindow.focus();
            printWindow.print();
            setTimeout(() => {
                if (iframe.parentNode) {
                    document.body.removeChild(iframe);
                }
            }, 1000);
        }, 500);
    };

    const handleArchive = async (asDraft = false) => {
        if (isSaving) return;
        if (!asDraft && isActionLocked) return;

        setIsSaving(true);
        setArchiveFeedback('idle');

        const htmlContent =
            printContainerRef.current?.innerHTML ||
            document.getElementById('pepapers-abmahnung-print')?.innerHTML ||
            '';

        try {
            const id = await savePePapersArchiveDoc({
                id: archiveDocId,
                documentType: 'abmahnung-ermahnung',
                status: asDraft ? 'entwurf' : 'final',
                salonName: betriebData.firmenname || 'Unbekannter Salon',
                mitarbeiterName: mitarbeiterData.name.trim(),
                confirmedDisclaimer: asDraft ? disclaimerAccepted : true,
                currentStep,
                payload: {
                    dokumentTyp,
                    istAuszubildender,
                    standardgrund,
                    sachverhaltFreitext,
                    betriebData,
                    mitarbeiterData: {
                        ...mitarbeiterData,
                        adresse: formatMitarbeiterAdresseInline(mitarbeiterData),
                    },
                    selectedTeamEmployeeId: selectedTeamEmployeeId || null,
                    generatedHtml: htmlContent,
                },
            });
            setArchiveDocId(id);
            setArchiveFeedback(asDraft ? 'draft' : 'success');
            onArchived?.();
            if (asDraft) {
                setTimeout(() => {
                    onClose();
                }, 600);
            } else {
                setTimeout(() => setArchiveFeedback('idle'), 3000);
            }
        } catch (error) {
            console.error('PEPapers Abmahnung Archivierung fehlgeschlagen:', error);
            setArchiveFeedback('error');
        } finally {
            setIsSaving(false);
        }
    };

    const renderBetriebField = (field: keyof BetriebFormData, label: string) => {
        const lockKey = field as keyof BetriebFieldLocks;
        const fieldId = `betrieb_${field}`;
        const isLocked = betriebLocks[lockKey];

        return (
            <div>
                <label className={LABEL_CLASS}>{label}</label>
                <input
                    type="text"
                    value={betriebData[field]}
                    readOnly={isLocked}
                    onChange={(e) => !isLocked && updateBetriebField(field, e.target.value)}
                    className={getInputClass(isLocked ? READONLY_INPUT_CLASS : INPUT_CLASS, fieldId)}
                />
                {renderFieldError(fieldId)}
            </div>
        );
    };

    const renderContractDocument = () => {
        const ortDatum = `${betriebData.ort || '____________'}, ${formatTodayGerman()}`;

        return (
            <div ref={printContainerRef} id="pepapers-abmahnung-print" className="w-full h-auto overflow-visible">
                <style>{AB_CONTRACT_PDF_CSS}</style>
                <div className={`${A4_PREVIEW_PAGE_CLASS} min-h-[297mm]`}>
                    <div className="pdf-letterhead text-center">
                        <h1>{dokumentTitel}</h1>
                        <h2>PreisExpert · PEPapers</h2>
                    </div>

                    <div className="av-meta-grid">
                        <div className="av-meta-col">
                            <p className="av-meta-label">Arbeitgeber</p>
                            <p>
                                <strong>{betriebData.firmenname || '____________'}</strong>
                                {betriebData.strasse ? (
                                    <>
                                        <br />
                                        {betriebData.strasse}
                                    </>
                                ) : null}
                                {betriebData.plz || betriebData.ort ? (
                                    <>
                                        <br />
                                        {[betriebData.plz, betriebData.ort].filter(Boolean).join(' ')}
                                    </>
                                ) : null}
                                <span className="av-meta-date">Datum: {formatTodayGerman()}</span>
                            </p>
                        </div>
                        <div className="av-meta-col">
                            <p className="av-meta-label">Arbeitnehmer/in</p>
                            <p>
                                <strong>{mitarbeiterData.name || '____________'}</strong>
                                {mitarbeiterData.strasse ? (
                                    <>
                                        <br />
                                        {mitarbeiterData.strasse}
                                    </>
                                ) : null}
                                {mitarbeiterData.plz || mitarbeiterData.ort ? (
                                    <>
                                        <br />
                                        {[mitarbeiterData.plz, mitarbeiterData.ort].filter(Boolean).join(' ')}
                                    </>
                                ) : !mitarbeiterData.strasse ? (
                                    <>
                                        <br />
                                        ____________
                                    </>
                                ) : null}
                            </p>
                        </div>
                    </div>

                    <div className="av-doc-section">
                        <h3>{dokumentTyp === 'abmahnung' ? 'Abmahnung' : 'Ermahnung'}</h3>
                        <p className={DOC_P_CLASS}>
                            Sehr geehrte/r Frau/Herr <strong>{mitarbeiterNameDoc}</strong>,
                        </p>
                        {isSonstigerGrund ? (
                            <p className={DOC_P_CLASS}>
                                hiermit erteilen wir Ihnen{istAuszubildender ? ' als Auszubildende(r)' : ''} eine
                                formelle {dokumentTitelKurz} aufgrund des nachfolgend dargestellten Sachverhalts.
                            </p>
                        ) : (
                            <p className={DOC_P_CLASS}>
                                hiermit erteilen wir Ihnen{istAuszubildender ? ' als Auszubildende(r)' : ''} eine
                                formelle {dokumentTitelKurz}. Grund: <strong>{grundLabel}</strong>.
                            </p>
                        )}
                    </div>

                    <div className="av-doc-section">
                        <h3>Sachverhalt</h3>
                        {!isSonstigerGrund ? (
                            <p className={DOC_P_CLASS}>Der konkrete Sachverhalt stellt sich wie folgt dar:</p>
                        ) : null}
                        <p className={`${DOC_P_CLASS} whitespace-pre-wrap`}>
                            {sachverhaltFreitext || '____________'}
                        </p>
                    </div>

                    <div className="av-doc-section">
                        <h3>Aufforderung &amp; Hinweis</h3>
                        {dokumentTyp === 'abmahnung' ? (
                            <>
                                <p className={DOC_P_CLASS}>{abmahnungAufforderung}</p>
                                <div className="av-warn-box">
                                    <strong>{abmahnungWarnfunktion}</strong>
                                </div>
                            </>
                        ) : (
                            <>
                                <p className={DOC_P_CLASS}>
                                    Wir bitten Sie nachdrücklich, derartige Verstöße künftig zu unterlassen und Ihren{' '}
                                    {pflichtenLabel} Pflichten sowie den betrieblichen Regelungen nachzukommen.
                                </p>
                                <p className={DOC_P_CLASS}>{ermahnungWiederholungshinweis}</p>
                            </>
                        )}
                    </div>

                    <table
                        className="ab-sig-table"
                        style={{
                            width: '100%',
                            marginTop: '28px',
                            borderCollapse: 'collapse',
                            tableLayout: 'fixed',
                        }}
                    >
                        <tbody>
                            <tr>
                                <td style={{ width: '50%', paddingRight: '20px', verticalAlign: 'top' }}>
                                    <p
                                        style={{
                                            margin: 0,
                                            minHeight: '34px',
                                            fontSize: '10.5px',
                                            lineHeight: 1.35,
                                            color: '#475569',
                                        }}
                                    >
                                        Ort, Datum: {ortDatum}
                                    </p>
                                    <div style={{ height: '36px' }} aria-hidden="true" />
                                    <div
                                        className="ab-sig-line-soft"
                                        style={{
                                            width: '200px',
                                            maxWidth: '100%',
                                            borderBottom: '2px solid #1e293b',
                                            marginBottom: '6px',
                                        }}
                                        aria-hidden="true"
                                    />
                                    <p style={{ margin: 0, fontSize: '11px', fontWeight: 600, color: '#0f172a' }}>
                                        Unterschrift Arbeitgeber/in
                                    </p>
                                    <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>
                                        {betriebData.firmenname || 'Arbeitgeber'}
                                    </p>
                                </td>
                                <td style={{ width: '50%', paddingLeft: '20px', verticalAlign: 'top' }}>
                                    <p
                                        style={{
                                            margin: 0,
                                            minHeight: '34px',
                                            fontSize: '10.5px',
                                            lineHeight: 1.35,
                                            color: '#475569',
                                        }}
                                    >
                                        Kenntnis genommen und Original erhalten am: ........................
                                    </p>
                                    <div style={{ height: '36px' }} aria-hidden="true" />
                                    <div
                                        className="ab-sig-line-soft"
                                        style={{
                                            width: '200px',
                                            maxWidth: '100%',
                                            borderBottom: '2px solid #1e293b',
                                            marginBottom: '6px',
                                        }}
                                        aria-hidden="true"
                                    />
                                    <p style={{ margin: 0, fontSize: '11px', fontWeight: 600, color: '#0f172a' }}>
                                        Unterschrift Arbeitnehmer/in (Empfangsbekenntnis)
                                    </p>
                                    <p
                                        style={{
                                            margin: 0,
                                            fontSize: '11px',
                                            color: '#64748b',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.04em',
                                        }}
                                    >
                                        {mitarbeiterData.name.trim() || 'Arbeitnehmer/in'}
                                    </p>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    if (!isOpen) return null;

    const modalWidthClass = currentStep === 2 ? 'max-w-4xl' : 'max-w-lg';

    return createPortal(
        <>
            <div
                className="fixed inset-0 bg-stone-900/70 dark:bg-black/90 z-[100000] backdrop-blur-md print:hidden"
                onClick={handleClose}
                aria-hidden="true"
            />
            <div className="fixed inset-x-0 top-[8.5rem] md:top-36 bottom-0 z-[100001] flex items-start justify-center overflow-hidden p-4 pb-8 font-sans print:inset-0 print:top-0 print:overflow-visible print:p-0">
                <div
                    className={`bg-pe-card dark:bg-[#111] rounded-2xl shadow-[0_0_40px_rgba(230,194,153,0.1)] border border-[#E6C299]/30 w-full ${modalWidthClass} max-h-[calc(100vh-9.5rem)] md:max-h-[calc(100vh-10rem)] flex min-h-0 flex-col justify-between overflow-hidden relative transition-all duration-200 my-2 print:shadow-none print:border-0 print:max-w-none print:max-h-none print:rounded-none print:my-0 print:bg-white`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <button
                        type="button"
                        onClick={handleClose}
                        className="absolute top-4 right-4 text-pe-muted dark:text-slate-400 hover:text-pe-text dark:hover:text-white bg-pe-elevated dark:bg-[#1a1a1a] hover:bg-pe-hover dark:hover:bg-[#222] rounded-lg p-2 transition-colors border border-pe-border-strong dark:border-[#333] z-[60] print:hidden"
                        aria-label="Schließen"
                    >
                        <X className="w-4 h-4" />
                    </button>

                    <div className="bg-pe-elevated dark:bg-[#111622] z-50 shrink-0 print:hidden border-b border-pe-border dark:border-[#222]">
                        <div className="px-6 pt-10 pb-4">
                            <h3 className="text-lg font-bold text-pe-accent-text dark:text-[#E6C299] text-center uppercase tracking-widest pr-8">
                                {dokumentTitel}
                            </h3>
                            <p className="text-center text-xs text-pe-muted dark:text-slate-400 uppercase tracking-widest mt-2">
                                Schritt {currentStep} von 2: {STEP_LABELS[currentStep]}
                            </p>
                        </div>
                    </div>

                    <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 custom-scrollbar print:p-0 print:overflow-visible">
                        {currentStep === 1 && (
                            <div className="space-y-6">
                                <div>
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/80 mb-3 border-b border-pe-border dark:border-[#222] pb-2">
                                        Dokumenten-Typ
                                    </p>
                                    {renderSegmentedControl<DokumentTyp>(
                                        dokumentTyp,
                                        [
                                            { key: 'ermahnung', label: 'Ermahnung' },
                                            { key: 'abmahnung', label: 'Abmahnung' },
                                        ],
                                        setDokumentTyp
                                    )}
                                    <p className="mt-2 text-[10px] text-pe-muted dark:text-slate-500">
                                        Zuerst wählen: Ermahnung (mild) oder Abmahnung (förmlich).
                                    </p>
                                    <label className="mt-3 flex items-center gap-2.5 cursor-pointer select-none rounded-lg border border-pe-border dark:border-slate-700/80 bg-stone-100/60 dark:bg-black/40 px-3 py-2.5">
                                        <input
                                            type="checkbox"
                                            checked={istAuszubildender}
                                            onChange={(e) => setIstAuszubildender(e.target.checked)}
                                            className="h-4 w-4 rounded border-pe-border-strong dark:border-slate-600 bg-pe-input dark:bg-black text-pe-accent-text dark:text-[#E6C299] focus:ring-[#E6C299]/40 focus:ring-offset-0"
                                        />
                                        <span className="text-xs font-semibold text-pe-muted dark:text-slate-300">
                                            Für Auszubildende(n)
                                        </span>
                                    </label>
                                </div>

                                <div>
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/80 mb-3 border-b border-pe-border dark:border-[#222] pb-2">
                                        Betriebsdaten
                                    </p>
                                    <div className="space-y-4">
                                        {renderBetriebField('firmenname', 'Firma / Salonname')}
                                        {renderBetriebField('strasse', 'Straße & Hausnummer')}
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                            {renderBetriebField('plz', 'PLZ')}
                                            <div className="sm:col-span-2">{renderBetriebField('ort', 'Ort')}</div>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/80 mb-3 border-b border-pe-border dark:border-[#222] pb-2">
                                        Mitarbeiter-Daten
                                    </p>
                                    <div className="space-y-4">
                                        <div>
                                            <label className={LABEL_CLASS}>Aus Team wählen</label>
                                            <select
                                                value={selectedTeamEmployeeId}
                                                onChange={(e) => handleTeamEmployeeSelect(e.target.value)}
                                                className={INPUT_CLASS}
                                            >
                                                <option value="">— Manuell eintragen —</option>
                                                {teamEmployees.map((emp) => (
                                                    <option key={`${emp.pool}-${emp.id}`} value={emp.id}>
                                                        {emp.name}
                                                        {emp.pool === 'azubi'
                                                            ? ' (Azubi/Assistenz)'
                                                            : emp.pool === 'unproduktiv'
                                                              ? ' (unproduktiv)'
                                                              : ''}
                                                    </option>
                                                ))}
                                            </select>
                                            {teamEmployees.length === 0 ? (
                                                <p className="mt-1 text-[10px] text-pe-muted dark:text-slate-500">
                                                    Keine Personaldaten hinterlegt – bitte Name und Adresse manuell
                                                    eintragen.
                                                </p>
                                            ) : (
                                                <p className="mt-1 text-[10px] text-pe-muted dark:text-slate-500">
                                                    Name und Adresse können nach der Auswahl noch angepasst werden.
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <label className={LABEL_CLASS}>Name des Mitarbeiters</label>
                                            <input
                                                type="text"
                                                value={mitarbeiterData.name}
                                                onChange={(e) => updateMitarbeiterField('name', e.target.value)}
                                                className={getInputClass(INPUT_CLASS, 'mitarbeiter_name')}
                                            />
                                            {renderFieldError('mitarbeiter_name')}
                                        </div>
                                        <div>
                                            <label className={LABEL_CLASS}>Straße &amp; Hausnummer</label>
                                            <input
                                                type="text"
                                                value={mitarbeiterData.strasse}
                                                onChange={(e) => updateMitarbeiterField('strasse', e.target.value)}
                                                placeholder="z. B. Musterstraße 12"
                                                className={getInputClass(INPUT_CLASS, 'mitarbeiter_strasse')}
                                            />
                                            {renderFieldError('mitarbeiter_strasse')}
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                            <div>
                                                <label className={LABEL_CLASS}>PLZ</label>
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    value={mitarbeiterData.plz}
                                                    onChange={(e) => updateMitarbeiterField('plz', e.target.value)}
                                                    placeholder="12345"
                                                    className={getInputClass(INPUT_CLASS, 'mitarbeiter_plz')}
                                                />
                                                {renderFieldError('mitarbeiter_plz')}
                                            </div>
                                            <div className="sm:col-span-2">
                                                <label className={LABEL_CLASS}>Ort</label>
                                                <input
                                                    type="text"
                                                    value={mitarbeiterData.ort}
                                                    onChange={(e) => updateMitarbeiterField('ort', e.target.value)}
                                                    placeholder="Musterstadt"
                                                    className={getInputClass(INPUT_CLASS, 'mitarbeiter_ort')}
                                                />
                                                {renderFieldError('mitarbeiter_ort')}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299]/80 mb-3 border-b border-pe-border dark:border-[#222] pb-2">
                                        Grund des Vorfalls
                                    </p>
                                    <div className="space-y-4">
                                        <div>
                                            <label className={LABEL_CLASS}>Standardgrund</label>
                                            <select
                                                value={standardgrund}
                                                onChange={(e) => {
                                                    setStandardgrund(e.target.value as StandardgrundKey);
                                                    clearFieldError('standardgrund');
                                                }}
                                                className={getInputClass(INPUT_CLASS, 'standardgrund')}
                                            >
                                                <option value="">Bitte wählen …</option>
                                                {STANDARDGRUND_OPTIONS.map((opt) => (
                                                    <option key={opt.key} value={opt.key}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </select>
                                            {renderFieldError('standardgrund')}
                                        </div>
                                        <div>
                                            <label className={LABEL_CLASS}>
                                                Konkretisierung des Vorfalls (Datum, Uhrzeit, Sachverhalt)
                                            </label>
                                            <textarea
                                                value={sachverhaltFreitext}
                                                onChange={(e) => {
                                                    setSachverhaltFreitext(e.target.value);
                                                    clearFieldError('sachverhalt_freitext');
                                                }}
                                                rows={5}
                                                placeholder="z. B. Am 12.03.2026 um 09:15 Uhr erschienen Sie ohne vorherige Absprache 25 Minuten zu spät zum Dienstbeginn …"
                                                className={getInputClass(
                                                    `${INPUT_CLASS} resize-y min-h-[120px]`,
                                                    'sachverhalt_freitext'
                                                )}
                                            />
                                            {renderFieldError('sachverhalt_freitext')}
                                            <p className="mt-3 text-xs text-pe-muted dark:text-slate-400 leading-relaxed border border-[#E6C299]/15 bg-[#E6C299]/5 rounded-lg px-3 py-2.5">
                                                {KI_LEKTORAT_HINWEIS}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {currentStep === 2 && (
                            <div className="w-full min-h-0 pr-2 mb-6">
                                {apiKiError && (
                                    <p className="mb-4 text-xs text-amber-400 border border-amber-500/30 bg-amber-500/10 rounded-lg px-3 py-2">
                                        KI-Lektorat fehlgeschlagen — Ihr eingetragener Sachverhalt wird unverändert verwendet.
                                    </p>
                                )}
                                {renderContractDocument()}
                            </div>
                        )}
                    </div>

                    {currentStep === 2 && (
                        <PePapersDisclaimerGate
                            accepted={disclaimerAccepted}
                            onAcceptedChange={setDisclaimerAccepted}
                        />
                    )}

                    <div className="px-6 py-4 border-t border-pe-border dark:border-[#222] shrink-0 print:hidden">
                        <div className="flex flex-wrap gap-3">
                            {currentStep > 1 && (
                                <button
                                    type="button"
                                    onClick={handleZurueck}
                                    className="flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-pe-border-strong dark:border-[#444] text-pe-muted dark:text-gray-300 transition-colors"
                                >
                                    Zurück
                                </button>
                            )}

                            {currentStep === 1 ? (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => void handleArchive(true)}
                                        disabled={isSaving || isOptimizingSachverhalt}
                                        className="flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-pe-border-strong dark:border-[#444] text-pe-muted dark:text-gray-300 transition-colors disabled:opacity-50"
                                    >
                                        {isSaving && archiveFeedback !== 'draft' ? 'Speichert...' : 'Später fortsetzen'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleWeiter}
                                        disabled={isOptimizingSachverhalt}
                                        className="flex-1 min-w-[120px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest bg-[#E6C299] hover:bg-[#d9b183] dark:hover:bg-white text-black transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                    >
                                        {isOptimizingSachverhalt ? (
                                            <>
                                                <Loader2 size={16} className="animate-spin" />
                                                Sachverhalt wird lektoriert...
                                            </>
                                        ) : (
                                            'Dokument erstellen'
                                        )}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => void handleArchive(true)}
                                        disabled={isSaving}
                                        className="flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-pe-border-strong dark:border-[#444] text-pe-muted dark:text-gray-300 transition-colors disabled:opacity-50"
                                    >
                                        {archiveFeedback === 'draft' ? 'Gespeichert! ✓' : 'Später fortsetzen'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void handleArchive(false)}
                                        disabled={isActionLocked || isSaving}
                                        className={getActionButtonClass(
                                            'flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-pe-card dark:bg-[#15171e] hover:bg-pe-hover dark:hover:bg-[#1a1d24] border-[#E6C299]/40 text-pe-accent-text dark:text-[#E6C299] transition-colors'
                                        )}
                                    >
                                        {isSaving && archiveFeedback !== 'draft'
                                            ? 'Speichert...'
                                            : archiveFeedback === 'success'
                                            ? 'Gespeichert! ✓'
                                            : 'Im Archiv speichern'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void handleDownloadPdf()}
                                        disabled={isActionLocked || isPdfDownloading}
                                        className={getActionButtonClass(
                                            'flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest bg-gradient-to-b from-[#E6C299] to-[#b48a56] hover:from-[#f0d3b3] hover:to-[#cfa674] text-black transition-colors'
                                        )}
                                    >
                                        {isPdfDownloading ? 'PDF wird erstellt...' : 'Als PDF herunterladen'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handlePrintIsolated}
                                        disabled={isActionLocked || isPdfDownloading}
                                        className={getActionButtonClass(
                                            'flex-1 min-w-[140px] px-4 py-3 font-bold rounded-xl text-xs uppercase tracking-widest border bg-transparent hover:bg-pe-hover dark:hover:bg-[#222] border-[#E6C299]/40 text-pe-accent-text dark:text-[#E6C299] transition-colors'
                                        )}
                                    >
                                        Drucken
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
};

export default AbmahnungModal;
